import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { CartService, CartState } from '../services/CartService.ts';
import { MenuProduct } from '../services/MenuService.ts';
import { CheckoutService, CheckoutMetadata } from '../services/CheckoutService.ts';

interface CartContextType {
  cart: CartState | null;
  isCartOpen: boolean;
  isLoading: boolean;
  error: string | null;
  currentRetailerId: string | null;
  // Cart actions
  createCart: (pricingType: 'MEDICAL' | 'RECREATIONAL') => Promise<CartState>;
  addToCart: (product: MenuProduct, variant: any, quantity?: number) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeFromCart: (itemId: string) => Promise<void>;
  clearCart: () => void;
  setRetailerId: (retailerId: string) => void;
  // Checkout actions
  initiateCheckout: (orderType: 'DELIVERY' | 'PICKUP', metadata?: CheckoutMetadata) => Promise<void>;
  // UI actions
  toggleCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  clearError: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const [cart, setCart] = useState<CartState | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPricingType, setCurrentPricingType] = useState<'MEDICAL' | 'RECREATIONAL' | null>(null);
  const [currentRetailerId, setCurrentRetailerId] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const setRetailerId = useCallback((retailerId: string) => {
    // If the retailer ID changed, the existing cart belongs to a different store — clear it
    if (currentRetailerId && currentRetailerId !== retailerId) {
      setCart(null);
    }
    setCurrentRetailerId(retailerId);
    // Store retailer ID in localStorage for persistence
    localStorage.setItem('currentRetailerId', retailerId);
  }, [currentRetailerId]);

  // Validate localStorage pricing type — guard against corrupted values
  const getValidPricingType = (value: string | null): 'MEDICAL' | 'RECREATIONAL' => {
    if (value === 'MEDICAL' || value === 'RECREATIONAL') return value;
    return 'RECREATIONAL';
  };

  // Check if cart data is stale (older than 7 days)
  const isCartStale = (metadata: any): boolean => {
    if (!metadata || !metadata.lastUpdated) return true;
    const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
    return (Date.now() - metadata.lastUpdated) > sevenDaysInMs;
  };

  // Restore cart on page load
  React.useEffect(() => {
    const restoreCart = async () => {
      try {
        setIsLoading(true);

        // Check if we have cart metadata to validate freshness
        const cartMetadataStr = localStorage.getItem('cartMetadata');
        if (cartMetadataStr) {
          const cartMetadata = JSON.parse(cartMetadataStr);
          if (isCartStale(cartMetadata)) {
            localStorage.removeItem(`cartId_MEDICAL`);
            localStorage.removeItem(`cartId_RECREATIONAL`);
            localStorage.removeItem('cartMetadata');
            localStorage.removeItem('cartPricingType');
            return;
          }
        }

        // Get stored pricing type and cart ID
        const storedPricingType = localStorage.getItem('cartPricingType') as 'MEDICAL' | 'RECREATIONAL';
        if (!storedPricingType) {
          localStorage.setItem('cartPricingType', 'RECREATIONAL');
          return;
        }

        const storedCartId = localStorage.getItem(`cartId_${storedPricingType}`);
        if (!storedCartId) {
          return;
        }

        // Get stored retailer ID - required for cart operations
        const storedRetailerId = localStorage.getItem('currentRetailerId');
        if (!storedRetailerId) {
          return;
        }

        // Try to fetch the stored cart
        const restoredCart = await CartService.getCart(storedCartId, storedRetailerId);
        setCart(restoredCart);
        setCurrentPricingType(storedPricingType);
        setCurrentRetailerId(storedRetailerId);

      } catch (error) {
        // Only clear stored cart data when the cart is genuinely gone (4xx errors).
        // Preserve data on transient failures (network, 500) so the cart survives outages.
        const status = (error as any)?.status;
        const isStaleCartError = status === 404 || status === 400;
        if (isStaleCartError) {
          // Only clear the failing pricing type's cart — leave the sibling cart
          // and the user's pricingType preference intact.
          const failedPricingType = localStorage.getItem('cartPricingType') as 'MEDICAL' | 'RECREATIONAL' | null;
          if (failedPricingType) {
            localStorage.removeItem(`cartId_${failedPricingType}`);
          }
          localStorage.removeItem('cartMetadata');
        }
      } finally {
        setIsLoading(false);
      }
    };

    restoreCart();
  }, []); // Only run once on mount

  // Enhanced cart persistence - save cart state more frequently
  React.useEffect(() => {
    if (cart && currentPricingType) {
      // Save cart ID and timing info for better restoration
      const cartMetadata = {
        id: cart.id,
        lastUpdated: Date.now(),
        itemCount: cart.items?.length || 0,
        pricingType: cart.pricingType
      };

      localStorage.setItem(`cartId_${currentPricingType}`, cart.id);
      localStorage.setItem('cartMetadata', JSON.stringify(cartMetadata));
    }
  }, [cart, currentPricingType]);

  // Handle page unload - ensure cart data is saved before leaving
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      if (cart && currentPricingType) {
        // Ensure cart data is saved before page unload
        localStorage.setItem(`cartId_${currentPricingType}`, cart.id);
        localStorage.setItem('cartPricingType', currentPricingType);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [cart, currentPricingType]);

  const createCart = useCallback(async (pricingType: 'MEDICAL' | 'RECREATIONAL' = 'RECREATIONAL'): Promise<CartState> => {
    try {
      setIsLoading(true);
      setError(null);

      const newCart = await CartService.createCart(pricingType, currentRetailerId || undefined);
      setCart(newCart);

      // Store cart ID in localStorage for persistence (separate for each pricing type)
      localStorage.setItem(`cartId_${pricingType}`, newCart.id);
      localStorage.setItem('cartPricingType', pricingType);

      return newCart;
    } catch (err) {
      console.error('Error creating cart:', err);
      setError(err instanceof Error ? err.message : 'Failed to create cart');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentRetailerId]);

  const addToCart = useCallback(async (product: MenuProduct, variant: any, quantity: number = 1) => {
    // Check if we have a retailer ID
    if (!currentRetailerId) {
      setError('No retailer selected. Please select a store location.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      let currentCart = cart;

      // Determine the active pricing type — prefer React state, fall back to localStorage
      const activePricingType = currentPricingType
        || getValidPricingType(localStorage.getItem('cartPricingType'));

      // If we have a cart but it's for a different pricing type, discard it
      if (currentCart && currentCart.pricingType !== activePricingType) {
        currentCart = null;
        setCart(null);
      }

      // Create cart if it doesn't exist
      if (!currentCart) {
        // Sync localStorage to the active pricing type
        localStorage.setItem('cartPricingType', activePricingType);

        // Try to get stored cart for current pricing type
        const storedCartId = localStorage.getItem(`cartId_${activePricingType}`);
        if (storedCartId) {
          try {
            currentCart = await CartService.getCart(storedCartId, currentRetailerId);
            setCart(currentCart);
          } catch (getError) {
            // Stored cart is stale/invalid — clear it and create fresh
            localStorage.removeItem(`cartId_${activePricingType}`);
            currentCart = await createCart(activePricingType);
          }
        } else {
          currentCart = await createCart(activePricingType);
        }
      }

      if (!currentCart) {
        throw new Error('Cart not available');
      }

      // Store pricing fallback data BEFORE making the API call
      const pricingFallback = {
        productId: product.id,
        option: variant.option,
        priceMed: variant.priceMed,
        priceRec: variant.priceRec,
        specialPriceMed: variant.specialPriceMed,
        specialPriceRec: variant.specialPriceRec,
        pricingType: currentCart.pricingType
      };

      // Store original pricing information for discount display
      const itemKey = `${product.id}-${variant.option}`;
      const originalPricing = {
        regularPrice: currentCart.pricingType === 'MEDICAL' ? variant.priceMed : variant.priceRec,
        specialPrice: currentCart.pricingType === 'MEDICAL' ? variant.specialPriceMed : variant.specialPriceRec,
        pricingType: currentCart.pricingType
      };

      if (originalPricing.regularPrice) {
        localStorage.setItem(`pricing_${itemKey}`, JSON.stringify(originalPricing));
      }

      const existingFallbacks = JSON.parse(localStorage.getItem('cartPricingFallbacks') || '[]');
      const updatedFallbacks = existingFallbacks.filter((fb: any) =>
        !(fb.productId === product.id && fb.option === variant.option)
      );
      updatedFallbacks.push(pricingFallback);
      localStorage.setItem('cartPricingFallbacks', JSON.stringify(updatedFallbacks));

      const updatedCart = await CartService.addItem(
        currentCart.id,
        product.id,
        variant.option,
        quantity,
        currentRetailerId
      );

      setCart(updatedCart);

    } catch (err) {
      console.error('Error adding item to cart:', err);

      // If we get a 500 error, the cart might be stale - clear it and retry once
      if (err instanceof Error && err.message.includes('500')) {
        const retryPricingType = currentPricingType
          || getValidPricingType(localStorage.getItem('cartPricingType'));
        localStorage.removeItem(`cartId_${retryPricingType}`);
        setCart(null);

        try {
          // Retry once with a fresh cart
          const freshCart = await createCart(retryPricingType);
          const retryUpdatedCart = await CartService.addItem(
            freshCart.id,
            product.id,
            variant.option,
            quantity,
            currentRetailerId
          );
          setCart(retryUpdatedCart);
          return;
        } catch (retryErr) {
          console.error('Retry also failed:', retryErr);
          setError('Failed to add item to cart after retry');
          return;
        }
      }

      setError(err instanceof Error ? err.message : 'Failed to add item to cart');
    } finally {
      setIsLoading(false);
    }
  }, [cart, createCart, currentRetailerId, currentPricingType]);

  const updateQuantity = useCallback(async (itemId: string, quantity: number) => {
    if (!cart) {
      setError('No cart available');
      return;
    }

    if (!currentRetailerId) {
      setError('No retailer selected. Please select a store location.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const updatedCart = await CartService.updateQuantity(cart.id, itemId, quantity, currentRetailerId);
      setCart(updatedCart);
    } catch (err) {
      console.error('Error updating item quantity:', err);
      setError(err instanceof Error ? err.message : 'Failed to update item quantity');
    } finally {
      setIsLoading(false);
    }
  }, [cart, currentRetailerId]);

  const removeFromCart = useCallback(async (itemId: string) => {
    if (!cart) {
      setError('No cart available');
      return;
    }

    if (!currentRetailerId) {
      setError('No retailer selected. Please select a store location.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const updatedCart = await CartService.removeItem(cart.id, itemId, currentRetailerId);
      setCart(updatedCart);

      // Clean up pricing fallback data for removed items
      try {
        const existingFallbacks = JSON.parse(localStorage.getItem('cartPricingFallbacks') || '[]');
        const remainingItemIds = new Set(updatedCart.items.map(item => `${item.productId}-${item.option}`));
        const filteredFallbacks = existingFallbacks.filter((fb: any) =>
          remainingItemIds.has(`${fb.productId}-${fb.option}`)
        );
        localStorage.setItem('cartPricingFallbacks', JSON.stringify(filteredFallbacks));
      } catch (error) {
        // Silently handle cleanup errors
      }
    } catch (err) {
      console.error('Error removing item from cart:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove item from cart');
    } finally {
      setIsLoading(false);
    }
  }, [cart, currentRetailerId]);

  const toggleCart = useCallback(() => {
    setIsCartOpen(prev => !prev);
  }, []);

  const openCart = useCallback(() => {
    setIsCartOpen(true);
  }, []);

  const closeCart = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  const clearCart = useCallback(() => {
    // Clear cart state
    setCart(null);
    setIsCartOpen(false);

    // Clear all cart-related localStorage data
    const currentPricingType = localStorage.getItem('cartPricingType') as 'MEDICAL' | 'RECREATIONAL' || 'RECREATIONAL';
    localStorage.removeItem(`cartId_${currentPricingType}`);
    localStorage.removeItem('cartMetadata');
    localStorage.removeItem('cartPricingFallbacks');

    // Clear any product-specific pricing data
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('pricing_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }, []);

  const initiateCheckout = useCallback(async (
    orderType: 'DELIVERY' | 'PICKUP', 
    metadata?: CheckoutMetadata
  ) => {
    if (!cart) {
      setError('No cart available for checkout');
      return;
    }

    if (!currentRetailerId) {
      setError('No retailer selected - please refresh the page');
      return;
    }

    if (!cart.items || cart.items.length === 0) {
      setError('Cart is empty - add items before checkout');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Validate checkout request
      CheckoutService.validateCheckoutRequest(cart.id, orderType, cart.pricingType, currentRetailerId!);

      // Create checkout session
      const checkoutSession = await CheckoutService.createCheckout({
        cartId: cart.id,
        orderType,
        pricingType: cart.pricingType,
        retailerId: currentRetailerId!,
        returnUrl: `${window.location.origin}/order-confirmation`,
        metadata
      });

      // Redirect to Dutchie's hosted checkout
      CheckoutService.initiateCheckout(checkoutSession);

    } catch (err) {
      console.error('Error initiating checkout:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to initiate checkout';
      
      // Show user-friendly error message
      if (errorMessage.includes('not yet implemented')) {
        setError('Checkout is being set up. Please call the store to complete your order or try again later.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [cart, currentRetailerId]);

  // Monitor pricing type changes and switch carts
  React.useEffect(() => {
    const handlePricingTypeChange = (event: CustomEvent) => {
      const newPricingType = event.detail.pricingType as 'MEDICAL' | 'RECREATIONAL';

      if (newPricingType && newPricingType !== currentPricingType) {
        setCurrentPricingType(newPricingType);

        // Clear current cart and load cart for new pricing type
        setCart(null);

        const storedCartId = localStorage.getItem(`cartId_${newPricingType}`);
        if (storedCartId && currentRetailerId) {
          CartService.getCart(storedCartId, currentRetailerId)
            .then(storedCart => {
              setCart(storedCart);
            })
            .catch(() => {
              localStorage.removeItem(`cartId_${newPricingType}`);
            });
        }
      }
    };

    // Initial pricing type and retailer ID setup
    const initialPricingType = localStorage.getItem('cartPricingType') as 'MEDICAL' | 'RECREATIONAL';
    if (initialPricingType && !currentPricingType) {
      setCurrentPricingType(initialPricingType);
    }
    
    const initialRetailerId = localStorage.getItem('currentRetailerId');
    if (initialRetailerId && !currentRetailerId) {
      setCurrentRetailerId(initialRetailerId);
    }
    
    // Listen for pricing type changes
    window.addEventListener('pricingTypeChanged', handlePricingTypeChange as EventListener);
    
    return () => {
      window.removeEventListener('pricingTypeChanged', handlePricingTypeChange as EventListener);
    };
  }, [currentPricingType, currentRetailerId]);

  const value: CartContextType = useMemo(() => ({
    cart,
    isCartOpen,
    isLoading,
    error,
    currentRetailerId,
    createCart,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    setRetailerId,
    initiateCheckout,
    toggleCart,
    openCart,
    closeCart,
    clearError,
  }), [
    cart,
    isCartOpen,
    isLoading,
    error,
    currentRetailerId,
    createCart,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    setRetailerId,
    initiateCheckout,
    toggleCart,
    openCart,
    closeCart,
    clearError,
  ]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextType {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}